import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { getValuesFromEnum } from "@app/class/Utilities";
import { CustomFormValidators } from "@app/directives/_forms/form-validation.service";
import { SpecialUserPermissions } from "@app/enum/SpecialUserPermissions";
import { AccountUserType } from "@app/enum/UserType";
import { IUser } from "@app/interfaces/IUser";
import { AccountFeatureService, IAccountFeatureDescription, ISpecialUserPermissionDescriptionMap } from "@app/services/accountFeature.service";
import { UserService } from "@app/services/users/user.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import copy from "fast-copy";
import { Observable } from "rxjs";
import './user-editor.component.scss';
import { UserEditorService } from "./user-editor.service";
import { AccountFeatures } from "@app/enum/account/AccountFeature";
import { LoginService } from "@app/services/login.service";
import { switchMap } from "rxjs/operators";
import { ILoginResponse } from "@app/services/login.service.ajs";
import { IPhoneNumberOutput } from "@app/directives/_forms/smsInput/smsInput.interfaces";

@UntilDestroy()
@Component({
    selector: 'user-editor',
    template: require('./user-editor.component.html')
})
export class UserEditorComponent implements OnInit, OnChanges {
    @Input() user: IUser;
    @Input() users: IUser[];
    @Output() userSave = new EventEmitter<IUser>();

    amendedUser: IUser;

    userForm: FormGroup;

    AccountUserType = AccountUserType;
    SpecialUserPermission = SpecialUserPermissions;
    userTypes = getValuesFromEnum(AccountUserType);

    accountFeatureDescriptions$: Observable<IAccountFeatureDescription[]>;
    specialUserPermissionsDescriptionsMap: ISpecialUserPermissionDescriptionMap;

    currentUserOnFoundationPlatform: boolean;

    loading: boolean;

    constructor(
        private readonly accountFeatureService: AccountFeatureService,
        private readonly loginService: LoginService,
        private readonly userEditorService: UserEditorService,
        private readonly userService: UserService
    ) { }

    ngOnInit(): void {
        this.currentUserOnFoundationPlatform = this.userService.isCurrentUserOnFoundationPlatform();
        this.accountFeatureDescriptions$ = this.userEditorService.getFeatureDescriptions(this.user.CurrentAccountUserMapping.AccountId);
        this.specialUserPermissionsDescriptionsMap = this.accountFeatureService.getSpecialUserPermissionsDescriptionsMap();
    }

    ngOnChanges(): void {
        this.createForm();
    };

    onAllowAllVehiclesToggled(): void {
        this.amendedUser.VehicleIds = (this.amendedUser.CurrentAccountUserMapping.AllowAllVehicles) ? [] : this.user.VehicleIds;
    }

    onAllowAllDriversToggled(): void {
        this.amendedUser.DriverIds = (this.amendedUser.CurrentAccountUserMapping.AllowAllDrivers) ? [] : this.user.DriverIds;
    }

    onAllowAllGeofencesToggled(): void {
        this.amendedUser.GeofenceIds = (this.amendedUser.CurrentAccountUserMapping.AllowAllGeofences) ? [] : this.user.GeofenceIds;
    }

    onReadFeatureChanged(accountFeature: AccountFeatures, enabled: boolean): void {
        if (enabled) {            
            this.amendedUser.CurrentAccountUserMapping.ReadFeatures = this.amendedUser.CurrentAccountUserMapping.ReadFeatures | accountFeature;
        }
        else {
            this.amendedUser.CurrentAccountUserMapping.ReadFeatures = this.amendedUser.CurrentAccountUserMapping.ReadFeatures & ~accountFeature;
        }
    }

    onManageFeatureChanged(accountFeature: AccountFeatures, enabled: boolean): void {
        if (enabled) {
            this.amendedUser.CurrentAccountUserMapping.ManageFeatures = this.amendedUser.CurrentAccountUserMapping.ManageFeatures | accountFeature;            
        }
        else {
            this.amendedUser.CurrentAccountUserMapping.ManageFeatures = this.amendedUser.CurrentAccountUserMapping.ManageFeatures & ~accountFeature;            
        }
    }

    onSmsOutputChanged(output: IPhoneNumberOutput): void {
        if (!output.isValid) {
            return;
        }
        this.userForm.get('Phone').setValue(output.internationalized);
   }

    onCombinedManageFeatureChanged(accountFeature: AccountFeatures, enabled: boolean): void {
        this.onReadFeatureChanged(accountFeature, enabled);
        this.onManageFeatureChanged(accountFeature, enabled);
    }

    saveUser(): void {
        this.loading = true;
        this.userService.saveUserWithAccountMapping(this.amendedUser).pipe(untilDestroyed(this)).subscribe(userMapping => {
            if (this.amendedUser.Id === 0) {
                this.amendedUser.Id = userMapping.UserId;
                this.amendedUser.CurrentAccountUserMapping = userMapping;
            }

            this.userSave.emit(this.amendedUser);
            this.loading = false;
        });
    }

    loginAs(): void {
        this.loading = true;
        if (!this.currentUserOnFoundationPlatform) {
            this.loading = false;
            return;
        }

        this.userService.getLoginToken(this.user.Id)
            .pipe(
                untilDestroyed(this),
                switchMap((token: string) => {
                    return this.loginService.loginWithToken(token, true)
                }))
            .subscribe((response: ILoginResponse) => {
                this.loginService.switchAccount(this.user.CurrentAccountUserMapping.AccountId, true).then((loginResponse) => {
                    this.loading = false;
                })
            });
    }

    //--

    private setAmendedUser(): void {
        this.amendedUser = copy(this.user);
        this.amendedUser.newUserLanguageCode = localStorage.getItem('userSelectedLanguageCode') ?? 'en';
    }

    private createForm(): void {
        const emailValidators = [Validators.required, CustomFormValidators.emptyWhiteSpace, Validators.email];
        if (this.user?.Id <= 0) {
            emailValidators.push(CustomFormValidators.duplicateEmail(this.users))
        }

        this.userForm = new FormGroup({
            "Name": new FormControl(this.user.CurrentAccountUserMapping?.Name || "", [Validators.required, CustomFormValidators.emptyWhiteSpace]),
            "Email": new FormControl(this.user.Email || "", emailValidators),
            "Phone": new FormControl(this.user.CurrentAccountUserMapping?.Phone || "", [CustomFormValidators.emptyWhiteSpace])
        })

        this.setAmendedUser();

        this.userForm.statusChanges.pipe(untilDestroyed(this)).subscribe(changes => {

            this.amendedUser = {
                ...this.amendedUser,
                Email: this.userForm.value.Email,
                CurrentAccountUserMapping: {
                    ...this.amendedUser.CurrentAccountUserMapping,
                    Name: this.userForm.value.Name,
                    Phone: this.userForm.value.Phone
                }
            }
        })
    }

}
